import { approvalsService, type ApprovalReason, type ApprovalRequestView } from '../../../services/approvals/approvals.service';
import type { OrderlineView } from '../../../services/orderlines/orderlines.service';
import { ordersService } from '../../../services/orders/orders.service';

type CancelPaymentline = {
  status: string;
  canceledAt?: string | null;
};

export type CancelOrderlineSelection = {
  orderlineId: number;
  quantity: number;
};

export type CancelScope = 'items' | 'order';

export const isReceiptCancelableOrderline = (line: OrderlineView): boolean => (
  (line.status === 'sent' || line.status === 'skipped') && line.quantity > 0
);

export const buildCancelAllOrderlineSelections = (orderlines: OrderlineView[]): CancelOrderlineSelection[] => (
  orderlines
    .filter(isReceiptCancelableOrderline)
    .map((line) => ({ orderlineId: line.id, quantity: line.quantity }))
);

export const hasPaidPaymentline = (paymentlines: CancelPaymentline[]): boolean => (
  paymentlines.some((line) => line.status === 'done' && !line.canceledAt)
);

export const resolveCancelScope = (
  orderlines: OrderlineView[],
  items: CancelOrderlineSelection[],
): CancelScope => {
  const allCancelableSelections = buildCancelAllOrderlineSelections(orderlines);
  if (allCancelableSelections.length === 0 || items.length !== allCancelableSelections.length) {
    return 'items';
  }

  const quantityById = new Map(items.map((item) => [item.orderlineId, item.quantity]));
  return allCancelableSelections.every((item) => quantityById.get(item.orderlineId) === item.quantity)
    ? 'order'
    : 'items';
};

export const buildCancelApprovalPayload = (
  items: CancelOrderlineSelection[],
  cancelScope: CancelScope = 'items',
) => ({
  actionType: 'cancel' as const,
  items,
  ...(cancelScope === 'order' ? { cancelScope } : {}),
});

export const cancelOrderlinesAsManager = async (
  tenantId: string,
  orderId: number,
  items: CancelOrderlineSelection[],
  employeeSession: string,
  fullTableCancel = false,
): Promise<void> => {
  await ordersService.cancelOrderlines(tenantId, orderId, items, employeeSession, fullTableCancel);
};

export const logManagerCancelAction = async (
  tenantId: string,
  orderId: number,
  items: CancelOrderlineSelection[],
  employeeSession: string,
  cancelScope: CancelScope = 'items',
): Promise<ApprovalRequestView> => (
  approvalsService.create(tenantId, orderId, {
    actionType: 'cancel',
    reason: 'manager_action',
    note: null,
    payload: buildCancelApprovalPayload(items, cancelScope),
  }, employeeSession)
);

export const requestCancelApproval = async (
  tenantId: string,
  orderId: number,
  items: CancelOrderlineSelection[],
  employeeSession: string,
  reason: ApprovalReason = 'cancel_order',
  note: string | null = null,
  cancelScope: CancelScope = 'items',
): Promise<ApprovalRequestView> => (
  approvalsService.create(tenantId, orderId, {
    actionType: 'cancel',
    reason,
    note,
    payload: buildCancelApprovalPayload(items, cancelScope),
  }, employeeSession)
);
